import { ApiError, meta } from "@baton/api";
import type { Runtime } from "@baton/core";
import { createI18n, defaultLocale, directionOf, I18nProvider, loadAreaOnce, loadCatalogue, registerAppCatalogue, type Catalogue, type I18n } from "@baton/i18n";
import { appCatalogue } from "@baton/i18n/app";
import { adoptStyleNonce, LiveRegion, loadDisabledReasonTooltip, MotionBridge, ThemeProvider, toast, type ThemeState } from "@baton/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import { meQuery } from "./app/queries.ts";
import { RuntimeProvider } from "./app/RuntimeProvider.tsx";
import { createAppRuntime } from "./app/runtime.ts";
import { prefetchOnIdle } from "./app/prefetch.ts";
import { loadReauthDialog, ReauthGate } from "./auth/sign-in/ReauthGate.tsx";
import { VERIFY_AREA } from "./auth/verify/catalogue.ts";
import { takeVerifiedLanding } from "./auth/verify/landing.ts";
import { isUnverified } from "./auth/verify/useEmailVerification.ts";
import { browserPlatform } from "./platform/browser.ts";
import { registerServiceWorker } from "./platform/serviceWorker.ts";
import { createRouter } from "./routes.tsx";
import { primedSession } from "./prime.ts";
import { leaveForSignIn } from "./routes/session.ts";
import { Regions } from "./shell/layout/Regions.tsx";
import { initialThemeState, persistThemeState } from "./shell/state/themeControl.ts";
import type { TeamsTabSession } from "./teams/bootstrap.tsx";
import { inTeamsTab, TEAMS_TAB_PREFIX } from "./teams/tab.ts";
import { TeamsThemeFollower } from "./teams/ThemeFollower.tsx";
import "./styles/app.css";

/**
 * The authenticated entry: the provider stack (query client, i18n, theme, runtime, router)
 * of `plan.md` § Package layout over the runtime of `@baton/core` wired to the browser
 * platform, mounting the route table of `routes.tsx` (FR-015-001). The toast region and
 * the two live regions mount once here (FR-015-091, FR-015-102). Before the router mounts,
 * one `GET /me` decides between the shell and the sign-in screen: nobody signed in leaves with
 * the current path as `redirect`, and a signed-in person's body seeds the cache so the shell
 * mounts without a second round trip (`contracts/client-api.md` § Session lifecycle). That
 * request is the one `prime.ts` already has in flight; the plain client sends it only when the
 * priming module resolved to nothing, which is a transport failure or a status the shell's own
 * error state should render.
 *
 * Inside the Microsoft Teams tab (022's tab single sign-on, 2026-10-02), known by the route class
 * `/teams/tab` alone (`teams/tab.ts`), the session comes from the tab's bootstrap instead, loaded
 * as a chunk of its own so the Teams library never reaches a page outside the tab: it signs the
 * person in with their Teams identity, draws its own pages when that needs them, and settles once
 * a session exists. The application then mounts under the tab's basename, in the Teams theme,
 * following the host's changes, and registers no service worker, since a frame inside Teams has
 * nothing to offer the worker's offline copy.
 */
async function bootstrapSession(runtime: Runtime): Promise<boolean> {
  const primed = await primedSession();
  if (primed !== null) {
    if (primed.me === null) {
      leaveForSignIn();
      return false;
    }
    runtime.queryClient.setQueryData(meQuery(runtime).queryKey, primed.me);
    return true;
  }
  try {
    const me = await meta.me(runtime.client, { retry: false });
    runtime.queryClient.setQueryData(meQuery(runtime).queryKey, me.data);
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      leaveForSignIn();
      return false;
    }
    // Any other failure lets the shell render its own error state and Retry.
    return true;
  }
}

interface Mount {
  readonly runtime: Runtime;
  readonly i18n: I18n;
  readonly container: HTMLElement;
  readonly locale: string;
  readonly messages: Catalogue;
  readonly browserTimeZone: string;
  /** The Teams tab's session when the document loaded inside the tab, else null. */
  readonly tab: TeamsTabSession | null;
}

async function mountApp({ runtime, i18n, container, locale, messages, browserTimeZone, tab }: Mount): Promise<void> {
  // The unverified-address banner's words load before the shell mounts, so the banner is in the shell's
  // first frame. Loaded by the banner itself, the strip landed a moment after the shell drew and pushed
  // the page down under a pointer already on its way to a sidebar row, which then took the click meant
  // for the row it moved over (2026-09-29). A failed load is not the boot's failure: the banner loads
  // its words again when it renders, and shows once they arrive.
  if (isUnverified(runtime.queryClient.getQueryData(meQuery(runtime).queryKey))) await loadAreaOnce(locale, VERIFY_AREA).catch(() => undefined);
  runtime.offline.start();
  // The verification page is the public entry's; a signed-in person it verified lands here with its word (001 FR-153).
  const landing = takeVerifiedLanding();
  if (landing !== null) toast({ variant: "success", title: landing === "verified" ? i18n.t("auth.verify.verified") : i18n.t("auth.verify.alreadyVerified") });
  const onThemeChange = (state: ThemeState) => {
    persistThemeState(runtime, state);
  };
  if (tab === null) registerServiceWorker();
  prefetchOnIdle(loadReauthDialog);
  const theme = tab === null ? initialThemeState(runtime) : { ...initialThemeState(runtime), theme: tab.theme.current() };
  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={runtime.queryClient}>
        <RuntimeProvider runtime={runtime}>
          <I18nProvider locale={locale} messages={messages} workspaceTimeZone={browserTimeZone} browserTimeZone={browserTimeZone}>
            <ThemeProvider initial={theme} onChange={onThemeChange}>
              {tab !== null && <TeamsThemeFollower theme={tab.theme} />}
              <MotionBridge>
                <LiveRegion collapsed={(count) => i18n.t("shell.live.collapsed", { count })}>
                  <RouterProvider router={createRouter(tab === null ? undefined : TEAMS_TAB_PREFIX)} />
                  <ReauthGate />
                  <Regions />
                </LiveRegion>
              </MotionBridge>
            </ThemeProvider>
          </I18nProvider>
        </RuntimeProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
  tab?.ready();
}

async function start(): Promise<void> {
  // FR-015-151: the style nonce first, before anything renders an element that writes a <style>.
  adoptStyleNonce();
  // The disabled-reason tooltip's chunk, which the public entry leaves out for its budget, is read alongside the words, so every button of the first frame stands in its tooltip from its mount and none is remounted when the chunk lands (`packages/ui` `unavailable.tsx`). A failed read is not the boot's failure: a button that needs its reason reads it again.
  const reasonTooltip = loadDisabledReasonTooltip().catch(() => undefined);
  // The application's static words, which the sign-in and setup entries never carry (2026-09-28).
  registerAppCatalogue(appCatalogue);
  const locale = defaultLocale;
  const messages = await loadCatalogue(locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = directionOf(locale);
  const container = document.getElementById("root");
  if (container === null) throw new Error("the document has no #root element");
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // The strings the runtime raises outside React render through the same catalogue.
  const i18n = createI18n({ locale, messages, workspaceTimeZone: browserTimeZone, browserTimeZone });
  const runtime = createAppRuntime({
    platform: browserPlatform(),
    i18n,
    reload: () => {
      window.location.reload();
    },
  });
  let tab: TeamsTabSession | null = null;
  // The component gallery (`routes.tsx`, 2026-10-15) stands outside the shell and calls no endpoint, so it mounts with no session asked for: a visit with nobody signed in draws the gallery rather than leaving for the sign-in page.
  const gallery = (import.meta.env.DEV || import.meta.env.BATON_GALLERY === "1") && window.location.pathname === "/_gallery";
  if (gallery) {
    // No session: nothing below reads the cache's `me`.
  } else if (inTeamsTab()) {
    const { startTeamsTab } = await import("./teams/bootstrap.tsx");
    tab = await startTeamsTab({
      runtime,
      primed: async () => (await primedSession())?.me ?? null,
      seed: (me) => {
        runtime.queryClient.setQueryData(meQuery(runtime).queryKey, me);
      },
      mount: (page) => {
        const root = createRoot(container);
        root.render(page);
        return () => {
          root.unmount();
        };
      },
      locale,
      messages,
      browserTimeZone,
    });
    if (tab === null) return;
  } else if (!(await bootstrapSession(runtime))) return;
  await reasonTooltip;
  await mountApp({ runtime, i18n, container, locale, messages, browserTimeZone, tab });
}

void start();
